import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import {
  deleteRecoveryWebhook,
  getRecoveryWebhook,
  upsertRecoveryWebhook,
  type UpsertWebhookResponse,
} from '../webhooks/api';
import { updateOrgRecoveryMode, type RecoveryMode } from '../orgs/api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';

export function RecoveryPage() {
  const { org } = useCurrentOrg();
  const orgId = org?.id ?? '';
  const qc = useQueryClient();

  const mode: RecoveryMode = org?.recovery_mode ?? 'customer_managed';

  const setMode = useMutation({
    mutationFn: (next: RecoveryMode) => updateOrgRecoveryMode(orgId, next),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs'] }),
  });

  if (!org) return null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Recovery</h2>
        <p className="mt-1 text-sm text-ink-secondary">
          How the third Shamir share is custodied so a user can recover their wallet on a new device.
        </p>
      </div>

      <Card>
        <CardHeader title="Custody mode" />
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2">
            <ModeOption
              title="Customer managed"
              tag="recommended"
              body="Sigil sends the recovery share to your backend over a signed webhook. You store and serve it back at recovery time."
              selected={mode === 'customer_managed'}
              onSelect={() => setMode.mutate('customer_managed')}
              disabled={setMode.isPending}
            />
            <ModeOption
              title="Sigil managed"
              tag="centralised"
              body="Sigil holds the recovery share too, encrypted with a separate KEK. Less setup, but custody is now centralised."
              selected={mode === 'sigil_managed'}
              onSelect={() => setMode.mutate('sigil_managed')}
              disabled={setMode.isPending}
            />
          </div>
          {mode === 'sigil_managed' && (
            <div className="mt-5 rounded-md border border-warn/30 bg-warn/[0.06] p-4 text-sm text-ink-secondary">
              <p className="font-medium text-warn">Heads up — centralised custody.</p>
              <p className="mt-1.5">
                In this mode Sigil holds two of the three shares. We use a distinct KEK so a single key
                compromise can't unwrap both, but the trust boundary is now Sigil rather than your
                infrastructure. Pick customer-managed if you can run a small webhook endpoint.
              </p>
            </div>
          )}
          {setMode.isError && (
            <p className="mt-3 text-sm text-bad">
              {setMode.error instanceof Error ? setMode.error.message : 'Could not switch mode'}
            </p>
          )}
        </CardBody>
      </Card>

      {mode === 'customer_managed' ? (
        <CustomerManagedSection orgId={orgId} />
      ) : (
        <SigilManagedSection />
      )}
    </div>
  );
}

function CustomerManagedSection({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const recovery = useQuery({
    queryKey: ['webhook', orgId, 'recovery'],
    queryFn: () => getRecoveryWebhook(orgId),
    enabled: !!orgId,
    staleTime: 30_000,
  });
  const [url, setUrl] = useState('');
  const [justCreated, setJustCreated] = useState<UpsertWebhookResponse | null>(null);
  const upsert = useMutation({
    mutationFn: (newUrl: string) => upsertRecoveryWebhook(orgId, newUrl),
    onSuccess: async (res) => {
      setJustCreated(res);
      setUrl('');
      await qc.invalidateQueries({ queryKey: ['webhook', orgId, 'recovery'] });
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteRecoveryWebhook(orgId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhook', orgId, 'recovery'] }),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    upsert.mutate(url);
  }

  const existing = recovery.data ?? null;

  return (
    <>
      <Card>
        <CardHeader
          title="Recovery webhook"
          description="Sigil POSTs the recovery share to this URL when an end user creates a wallet, and reads it back when they go through recovery. Without it, wallet creation fails."
        />
        <CardBody>
          {existing && (
            <div className="mb-4 rounded-md border border-line-soft bg-surface-soft p-4">
              <p className="font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">
                Currently configured
              </p>
              <p className="mt-1 break-all font-mono text-xs text-ink-primary">{existing.url}</p>
              <p className="mt-1 text-[11px] text-ink-tertiary">
                Updated {new Date(existing.updated_at).toLocaleString()}
              </p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              label={existing ? 'Replace URL (and rotate secret)' : 'Webhook URL'}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://api.example.com/sigil/recovery"
              required
              type="url"
              hint="HTTPS only in production. Each save mints a fresh secret — your old one stops working."
            />
            {upsert.isError && (
              <p className="text-sm text-bad">
                {upsert.error instanceof Error ? upsert.error.message : 'Could not save'}
              </p>
            )}
            <div className="flex justify-end gap-2">
              {existing && !recovery.isLoading && (
                <Button type="button" variant="danger" onClick={() => remove.mutate()}>
                  Remove
                </Button>
              )}
              <Button type="submit" disabled={upsert.isPending || !url}>
                {upsert.isPending ? 'Saving…' : existing ? 'Replace and rotate' : 'Save webhook'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {justCreated && (
        <SecretCard cleartext={justCreated.secret} onDismiss={() => setJustCreated(null)} />
      )}
      <SignatureSpecCard />
    </>
  );
}

function SigilManagedSection() {
  return (
    <Card>
      <CardHeader
        title="No webhook required"
        description="Sigil custodies the recovery share for you. End users only need their email to recover."
      />
      <CardBody className="space-y-3 text-sm text-ink-secondary">
        <p>
          New wallets created from this point on will store their recovery share with Sigil, encrypted under
          a KEK distinct from the provider share. Wallets created before switching modes keep their original
          custody — flip back to customer-managed if you need to keep all wallets on one model.
        </p>
        <p>
          The recovery flow on the iframe is identical: the user enters an OTP, we hand back both shares,
          and the iframe rebuilds the private key in memory.
        </p>
      </CardBody>
    </Card>
  );
}

function ModeOption({
  title,
  tag,
  body,
  selected,
  onSelect,
  disabled,
}: {
  title: string;
  tag: string;
  body: string;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={`rounded-md border p-4 text-left transition-colors ${
        selected
          ? 'border-ink-primary ring-1 ring-ink-primary'
          : 'border-line-soft hover:border-line'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-ink-primary">{title}</span>
        <span className="rounded bg-surface-soft px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-ink-secondary">
          {tag}
        </span>
      </div>
      <p className="mt-2 text-xs text-ink-secondary">{body}</p>
    </button>
  );
}

function SecretCard({ cleartext, onDismiss }: { cleartext: string; onDismiss: () => void }) {
  return (
    <div className="rounded-xl border border-warn/30 bg-warn/[0.06] p-5 text-sm">
      <p className="font-medium text-warn">Copy this signing secret now — you won't see it again.</p>
      <pre className="mt-3 overflow-x-auto rounded border border-warn/30 bg-surface-white px-3 py-2 font-mono text-xs">
        {cleartext}
      </pre>
      <p className="mt-3 text-xs text-ink-secondary">
        Configure your backend to verify <code className="rounded bg-surface-soft px-1">X-Sigil-Signature</code>{' '}
        on every incoming request using this secret.
      </p>
      <button
        onClick={onDismiss}
        className="mt-3 text-xs font-medium text-warn underline-offset-4 hover:underline"
      >
        I saved it. Dismiss.
      </button>
    </div>
  );
}

function SignatureSpecCard() {
  return (
    <Card>
      <CardHeader title="How signatures work" description="Sigil signs every webhook call. Reject anything unsigned." />
      <CardBody className="space-y-4 text-sm text-ink-secondary">
        <p>
          Each request includes a <code className="rounded bg-surface-soft px-1 font-mono text-xs">X-Sigil-Signature</code>{' '}
          header in the form{' '}
          <code className="rounded bg-surface-soft px-1 font-mono text-xs">t=&lt;unix&gt;,v1=&lt;hex&gt;</code>.
        </p>
        <p>
          Reconstruct the signed payload as{' '}
          <code className="rounded bg-surface-soft px-1 font-mono text-xs">{`<t> + "." + <raw-body>`}</code>{' '}
          and compute <code className="rounded bg-surface-soft px-1 font-mono text-xs">hmac_sha256(payload, secret)</code>.
          Compare hex output to <code className="rounded bg-surface-soft px-1 font-mono text-xs">v1</code> in
          constant time.
        </p>
        <p>
          Reject if <code className="rounded bg-surface-soft px-1 font-mono text-xs">t</code> is more than ~5
          minutes off your clock. We will retry up to 3 times with exponential backoff on 5xx; treat the
          payload as idempotent on{' '}
          <code className="rounded bg-surface-soft px-1 font-mono text-xs">{`(wallet_id, polynomial_version)`}</code>.
        </p>
      </CardBody>
    </Card>
  );
}
